import { runInAction, observable, action, computed, makeObservable } from "mobx";
import { MrbBaseEditViewStore } from "mrb/core";
import { MrbFieldSelectStore } from "mrb/components/select";
import { UserEditForm } from "administration/user/forms";
import { localizationService } from "common/localization";
import { nakiRadioRoles } from "common/constants";
import { applicationErrorCodes } from "common/constants";
import { MrbModalParams } from "mrb/common/models";
import { filter, merge, some, find, map } from "lodash";

const USER_TYPE = {
    user: "user",
    administrator: "administrator",
    loadio: "loadio",
};

const USER_TYPE_OPTIONS = [
    { id: USER_TYPE.user, name: "User" },
    { id: USER_TYPE.administrator, name: "Administrator" },
    { id: USER_TYPE.loadio, name: "Loadio Administrator" },
];

class UserEditViewStore extends MrbBaseEditViewStore {
    administratorRole = null;
    loadioRole = null;
    producerRole = null;
    @observable guestEmail = null;
    @observable producers = [];

    @observable artistInfo = null;

    @observable userAccessType = null;

    constructor(rootStore, { routeStore }) {
        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            id: id,
            name: "user",
            FormClass: UserEditForm,
            autoFocusField: "firstName",
            navigateBack: "master.administration.user.list",
            successCreateNotification: null,
            successUpdateNotification: null,
            redirectOnUpdateSuccess: () => this.rootStore.routerStore.goTo("master.administration.user.list"),
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.isAdminItself = id === this.rootStore.userStore.user.id;
        this.passwordResetModal = new MrbModalParams();

        this.userTypeSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: false,
                virtualized: false,
                isClearable: false,
                placeholder: "Select user type...",
            },
            this.form.$("userType"),
            USER_TYPE_OPTIONS
        );
    }

    get(id) {
        return this.routeStore.getResource(id);
    }

    update(data) {
        const resource = merge({}, this.item);
        resource.firstName = data.firstName;
        resource.lastName = data.lastName;
        resource.email = data.email;

        resource.roles = filter(
            resource.roles,
            (role) => role.id !== this.administratorRole?.id && role.id !== this.loadioRole?.id
        );
        if (data.userType === USER_TYPE.administrator && this.administratorRole) {
            resource.roles.push(this.administratorRole);
        } else if (data.userType === USER_TYPE.loadio && this.loadioRole) {
            resource.roles.push(this.loadioRole);
        }

        return this.routeStore.update(resource);
    }

    async getResource(id) {
        const [user, roles] = await Promise.all([this.get(id), this.routeStore.getRoles()]);
        this.setGuestEmail(user.guestEmail);
        this.setArtistInfo({
            artistRegisterMessage: user.producerUserAccount?.artistRegisterMessage,
            producerType: user.producerUserAccount?.producerType,
            phoneNumber: user.producerUserAccount?.phoneNumber,
        });
        runInAction(() => {
            this.administratorRole = find(roles, (role) => role.name === nakiRadioRoles.administratorRoleName);
            this.loadioRole = find(roles, (role) => role.name === nakiRadioRoles.loadioRoleName);
            this.producerRole = find(roles, (role) => role.name === nakiRadioRoles.producerRoleName);
            this.setItem(user);
            this.userAccessType = user.nakiRadioUserAccessType;
            const isProducer = user.roles && this.producerRole && some(user.roles, (role) => role.id === this.producerRole.id);
            const isAdministrator =
                user.roles && this.administratorRole && some(user.roles, (role) => role.id === this.administratorRole.id);
            const isLoadio = user.roles && this.loadioRole && some(user.roles, (role) => role.id === this.loadioRole.id);
            if (isProducer) {
                map(user.producerUserAccount?.assignedProducerUserAccounts, (assignedProducerUserAccount) => {
                    const producer = assignedProducerUserAccount.producer;
                    this.producers.push({
                        producerName: producer.name,
                        producerId: producer.id,
                    });
                });
            }
            const userType = isAdministrator
                ? USER_TYPE.administrator
                : isLoadio
                ? USER_TYPE.loadio
                : USER_TYPE.user;
            this.updateForm({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                userType,
            });
            this.userTypeSelectStore.setSelectedItem(
                find(USER_TYPE_OPTIONS, (o) => o.id === userType)
            );
        });
    }

    onUpdateError(error) {
        if (error.data.errorCode === applicationErrorCodes.producer.administratorConflict) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.USER.EDIT.ERROR.PRODUCER_ADMIN_CONFLICT"),
                error
            );
        } else if (error.data.errorCode === applicationErrorCodes.producer.loadioAndProducerConflict) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.USER.EDIT.ERROR.PRODUCER_LOADIO_CONFLICT"),
                error
            );
        } else if (error.data.errorCode === applicationErrorCodes.producer.emailAlreadyExists) {
            this.form.$("email").invalidate("User with specified E-mail address already exists.");
        } else {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.USER.EDIT.ERROR.GENERIC_MESSAGE"),
                error
            );
        }
    }

    disapproveUser = () => {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Do you want to disapprove user?",
            onConfirm: async () => {
                await this.routeStore.disapprove(this.item);
                this.item.isApproved = false;
            },
            yesLabel: "Disapprove",
            noLabel: "Cancel",
            description: "A disapproved user won't be able to sign and use the application anymore.",
        });
    };

    approveUser = () => {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Do you want to approve user?",
            onConfirm: async () => {
                await this.routeStore.approve(this.item);
                this.item.isApproved = true;
            },
            yesLabel: "Approve",
            noLabel: "Cancel",
            description: "An approved user will be able to sign in and use the application.",
        });
    };

    lockUser = () => {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Do you want to lock user?",
            onConfirm: async () => {
                await this.routeStore.lock(this.item);
                this.item.isLockedOut = true;
            },
            yesLabel: "Lock",
            noLabel: "Cancel",
            description: "A locked user won't be able to sign in to the application using his current password.",
        });
    };

    unlockUser = () => {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Do you want to unlock user?",
            onConfirm: async () => {
                await this.routeStore.unlock(this.item);
                this.item.isLockedOut = false;
            },
            yesLabel: "Unlock",
            noLabel: "Cancel",
            description: "An unlocked user will be able to sign in to the application using his current password.",
        });
    };

    changePassword = () => {
        this.passwordResetModal.open({
            user: this.item,
            routeStore: this.routeStore,
        });
    };

    @computed get canImpersonate() {
        if (!this.item || !this.item.roles) {
            return false;
        }
        // Unapproved users cannot authenticate (the auth pipeline resolves users via
        // GetUserInfoAsync, which returns null for !IsApproved), so an impersonation
        // token for them would 404 / be rejected as "Unknown user".
        if (!this.item.isApproved) {
            return false;
        }
        if (this.isAdminItself) {
            return false;
        }
        const isAdministrator =
            this.administratorRole && some(this.item.roles, (role) => role.id === this.administratorRole.id);
        return !isAdministrator;
    }

    @action
    impersonateUser = async () => {
        try {
            await this.rootStore.impersonationStore.startImpersonation(this.item.id);
        } catch (error) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.USER.EDIT.ERROR.IMPERSONATION_FAILED"),
                error
            );
        }
    };

    @action.bound
    setGuestEmail(email) {
        this.guestEmail = email;
    }

    @action.bound
    setArtistInfo(value) {
        this.artistInfo = value;
    }
}

export { USER_TYPE };
export default UserEditViewStore;
